import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { Grid, Skeleton } from '@mui/material';
import { Field, Formik, FormikErrors, FormikHelpers } from 'formik';
import { useMutation, useQueryClient } from 'react-query';

import {
	CerebroPageContainer,
	CerebroBreadcrumb,
	CerebroHeading,
	CerebroText,
	CerebroLabel,
	CerebroInput,
	CerebroPrimaryButton,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroMultiDropdown,
	CerebroMultiDropdownMenuItem,
	CerebroYesNo,
	CerebroTextarea,
	NaicsCodeAutoComplete,
	CerebroAutoComplete,
	CerebroMaskedInput,
	CerebroForm,
} from '../shared/components';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { useApi, useAuth } from '../shared/context';
import { FormikUtils, NumberUtils } from '../shared/utils';
import { CountryTypeMap, CountryTypes, Deal, Industry, NaicsCode, State, UpdateDealDto } from '../shared/models';
import { getApiResponseData } from '../shared/utils/axios';
import {
	useDealGeographiesQuery,
	useDealQuery,
	useStatesQuery,
	useIndustriesQuery,
	useNavigateToNextPage,
	QueryKeys,
} from '../shared/hooks';

export const CompanyAcquisitionTarget: React.FC = () => {
	const snackbar = useSnackbar();

	const api = useApi();
	const { currentUser } = useAuth();

	const navigateToNextPage = useNavigateToNextPage();

	const { guid } = useParams<'guid'>();

	const queryClient = useQueryClient();

	const dealQuery = useDealQuery(guid);
	const statesQuery = useStatesQuery();
	const industriesQuery = useIndustriesQuery();
	const geographiesQuery = useDealGeographiesQuery(guid, 'Acquisition');

	const dealMutation = useMutation(
		async ({ values }: DealMutationVariables) => {
			const { acquisitionNaicsCode, acquisitionIndustry, ...dealValues } = values;

			const dealPatch: UpdateDealDto = {
				...dealValues,

				acquisitionYearsInOperation: NumberUtils.ParseDecimal(values.acquisitionYearsInOperation, 0),
				acquisitionUsBasedEmployees: NumberUtils.ParseDecimal(values.acquisitionUsBasedEmployees, 0),
				acquisitionNonUsBasedEmployees: NumberUtils.ParseDecimal(values.acquisitionNonUsBasedEmployees, 0),

				acquisitionIndustryGuid: acquisitionIndustry?.guid ?? undefined,
				acquisitionNaicsCode: acquisitionNaicsCode?.code ?? undefined,
				acquisitionCustomerConcentrationDetails: dealValues.acquisitionCustomerConcentrationsMoreThan10Percent
					? dealValues.acquisitionCustomerConcentrationDetails
					: '',
				acquisitionB2bPercent: NumberUtils.ParseDecimal(values.acquisitionB2bPercent, undefined),
				acquisitionB2cPercent: NumberUtils.ParseDecimal(values.acquisitionB2cPercent, undefined),
				acquisitionB2gPercent: NumberUtils.ParseDecimal(values.acquisitionB2gPercent, undefined),
				sourcePage: 'AcquisitionCompany',
			};

			const { data, error } = await getApiResponseData<Deal>(
				api.patch(CerebroApiEndpoints.Deals.Root(guid), dealPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: deal => {
				queryClient.setQueryData(QueryKeys.Deal.Root(guid), deal);

				queryClient.invalidateQueries(QueryKeys.Matching.Get(guid));
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	const dealGeographiesMutation = useMutation(
		async ({ values }: DealMutationVariables) => {
			const { data, error } = await getApiResponseData<State[]>(
				api.post(CerebroApiEndpoints.DealGeographies(guid, 'Acquisition'), values.geographies)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: geographies => {
				queryClient.setQueryData(QueryKeys.Geographies(guid, 'Acquisition'), geographies);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	const onSubmit = async (values: CompanyBorrowerFormValues, helpers: FormikHelpers<CompanyBorrowerFormValues>) => {
		var results = await Promise.allSettled([
			dealMutation.mutateAsync({ values, helpers }),
			dealGeographiesMutation.mutateAsync({ values, helpers }),
		]);

		helpers.setSubmitting(false);

		if (results.every(result => result.status === 'fulfilled')) {
			snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
			navigateToNextPage();
		} else {
			snackbar.enqueueSnackbar('Something went wrong, some of your changes could not be saved.', {
				variant: 'error',
			});
		}
	};

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Company &gt; Acquisition Target</CerebroBreadcrumb>
			<CerebroHeading>Tell us about the Acquisition Company</CerebroHeading>
			<CerebroText sx={{ marginTop: 3, marginBottom: 6 }}>
				Provide details on the company being acquired. All information is completely confidential and will help
				identify the best matching lenders for you.
			</CerebroText>

			<Formik
				validate={validateForm}
				enableReinitialize
				onSubmit={onSubmit}
				initialValues={{
					acquisitionNaicsCode: dealQuery.data?.acquisitionNaicsCode ?? null,
					acquisitionCountryType: dealQuery.data?.acquisitionCountryType ?? 'UnitedStates',
					acquisitionStateCode: dealQuery.data?.acquisitionState?.code ?? '',
					acquisitionPostalCode: dealQuery.data?.acquisitionPostalCode ?? '',
					geographies: geographiesQuery.data?.map(state => state.code) ?? ([] as string[]),
					acquisitionIndustry: dealQuery.data?.acquisitionIndustry ?? null,
					acquisitionYearsInOperation: NumberUtils.FormatDecimal(dealQuery.data?.acquisitionYearsInOperation),
					acquisitionUsBasedEmployees: NumberUtils.FormatDecimal(dealQuery.data?.acquisitionUsBasedEmployees),
					acquisitionNonUsBasedEmployees: NumberUtils.FormatDecimal(
						dealQuery.data?.acquisitionNonUsBasedEmployees
					),
					acquisitionInstitutionalInvestor: dealQuery.data?.acquisitionInstitutionalInvestor ?? false,
					acquisitionB2bPercent: NumberUtils.FormatDecimal(dealQuery.data?.acquisitionB2bPercent),
					acquisitionB2cPercent: NumberUtils.FormatDecimal(dealQuery.data?.acquisitionB2cPercent),
					acquisitionB2gPercent: NumberUtils.FormatDecimal(dealQuery.data?.acquisitionB2gPercent),
					acquisitionCustomerConcentrationsMoreThan10Percent:
						dealQuery.data?.acquisitionCustomerConcentrationsMoreThan10Percent ?? false,
					acquisitionCustomerConcentrationDetails:
						dealQuery.data?.acquisitionCustomerConcentrationDetails ?? '',
				}}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm onSubmit={handleSubmit}>
						{currentUser?.isEmployeeUser === true && (
							<>
								<CerebroLabel htmlFor="acquisitionNaicsCode">
									What is the company NAICS code?{' '}
									<CerebroText variant="hint" component="span">
										(Employee Only)
									</CerebroText>
								</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="acquisitionNaicsCode"
										name="acquisitionNaicsCode"
										component={NaicsCodeAutoComplete}
										placeholder="6 Digit NAICS Code"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('acquisitionNaicsCode', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
								{FormikUtils.IsInvalid('acquisitionNaicsCode', errors, touched) && (
									<CerebroText variant="error">{errors.acquisitionNaicsCode}</CerebroText>
								)}
							</>
						)}

						<CerebroLabel sx={{ marginTop: 3 }}>Borrowing Entity Location</CerebroLabel>

						<Grid container spacing={3}>
							<Grid item xs={4}>
								{dealQuery.isSuccess && statesQuery.isSuccess ? (
									<Field
										as={CerebroDropdown}
										name="acquisitionCountryType"
										disabled={isSubmitting}
										onChange={(e: any) => {
											const changed = e.target.value as CountryTypes;
											setFieldValue('acquisitionCountryType', changed);
											if (changed != 'UnitedStates') {
												setFieldValue('acquisitionStateCode', '');
												setFieldValue('acquisitionPostalCode', '');
											}
										}}
									>
										{Object.entries(CountryTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
							{values.acquisitionCountryType == 'UnitedStates' && (
								<Grid item xs={4}>
									{dealQuery.isSuccess && statesQuery.isSuccess ? (
										<Field as={CerebroDropdown} name="acquisitionStateCode" disabled={isSubmitting}>
											{statesQuery.data.map(state => (
												<CerebroDropdownMenuItem key={state.code} value={state.code}>
													{state.name}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									) : (
										<Skeleton height={60} />
									)}
								</Grid>
							)}
							{values.acquisitionCountryType == 'UnitedStates' && (
								<Grid item xs={4}>
									{dealQuery.isSuccess && statesQuery.isSuccess ? (
										<Field
											as={CerebroInput}
											name="acquisitionPostalCode"
											disabled={isSubmitting}
											placeholder="Zip Code"
										/>
									) : (
										<Skeleton height={60} />
									)}
								</Grid>
							)}
						</Grid>

						<CerebroLabel htmlFor="geographies" sx={{ marginTop: 3 }}>
							Other Operating Locations
						</CerebroLabel>
						{dealQuery.isSuccess && statesQuery.isSuccess ? (
							<Field
								id="geographies"
								as={CerebroMultiDropdown}
								name="geographies"
								disabled={isSubmitting}
							>
								{statesQuery.data.map(state => (
									<CerebroMultiDropdownMenuItem key={state.code} value={state.code}>
										{state.name}
									</CerebroMultiDropdownMenuItem>
								))}
							</Field>
						) : (
							<Skeleton height={60} />
						)}

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item xs={12} md={7}>
								<CerebroLabel
									required
									htmlFor="acquisitionIndustry"
									error={FormikUtils.IsInvalid('acquisitionIndustry', errors, touched)}
								>
									What is the company&apos;s industry?
								</CerebroLabel>
								{dealQuery.isSuccess && industriesQuery.isSuccess ? (
									<Field
										id="acquisitionIndustry"
										component={CerebroAutoComplete}
										name="acquisitionIndustry"
										disabled={isSubmitting}
										options={industriesQuery.data}
										isOptionEqualToValue={(option: Industry, value: Industry) =>
											option.guid === value.guid
										}
										getOptionLabel={(industry: Industry) => industry?.name ?? 'Select Industry'}
										error={FormikUtils.IsInvalid('acquisitionIndustry', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
								{FormikUtils.IsInvalid('acquisitionIndustry', errors, touched) && (
									<CerebroText variant="error">{errors.acquisitionIndustry}</CerebroText>
								)}
							</Grid>

							<Grid item xs={12} md={7}>
								<CerebroLabel required htmlFor="acquisitionYearsInOperation">
									How many years has the acquisition target been operating?
								</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="acquisitionYearsInOperation"
										as={CerebroMaskedInput}
										name="acquisitionYearsInOperation"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('acquisitionYearsInOperation', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
								{FormikUtils.IsInvalid('acquisitionYearsInOperation', errors, touched) && (
									<CerebroText variant="error">{errors.acquisitionYearsInOperation}</CerebroText>
								)}
							</Grid>
						</Grid>

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item xs={12} md={6}>
								<CerebroLabel required id="acquisitionUsBasedEmployees">
									How many US employees?
								</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="acquisitionUsBasedEmployees"
										as={CerebroMaskedInput}
										name="acquisitionUsBasedEmployees"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('acquisitionUsBasedEmployees', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
								{FormikUtils.IsInvalid('acquisitionUsBasedEmployees', errors, touched) && (
									<CerebroText variant="error">{errors.acquisitionUsBasedEmployees}</CerebroText>
								)}
							</Grid>

							<Grid item xs={12} md={6}>
								<CerebroLabel id="acquisitionNonUsBasedEmployees">
									How many non-US employees?
								</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="acquisitionNonUsBasedEmployees"
										as={CerebroMaskedInput}
										disabled={isSubmitting}
										name="acquisitionNonUsBasedEmployees"
										error={FormikUtils.IsInvalid('acquisitionNonUsBasedEmployees', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
								{FormikUtils.IsInvalid('acquisitionNonUsBasedEmployees', errors, touched) && (
									<CerebroText variant="error">{errors.acquisitionNonUsBasedEmployees}</CerebroText>
								)}
							</Grid>
						</Grid>

						<CerebroLabel id="acquisitionInstitutionalInvestor" sx={{ marginTop: 3 }}>
							Is the company backed by an institutional investor?
						</CerebroLabel>
						{dealQuery.isSuccess ? (
							<Field
								id="acquisitionInstitutionalInvestor"
								as={CerebroYesNo}
								name="acquisitionInstitutionalInvestor"
								disabled={isSubmitting}
							/>
						) : (
							<Skeleton height={60} />
						)}

						<CerebroText sx={{ marginTop: 3 }}>
							What percentage of your business comes from each of the following?{' '}
							<CerebroText component="span" variant="hint">
								(answers should total 100)
							</CerebroText>
						</CerebroText>
						<Grid container spacing={3}>
							<Grid item xs={12} md={4}>
								<CerebroLabel id="acquisitionB2bPercent">B2B</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="acquisitionB2bPercent"
										as={CerebroMaskedInput}
										name="acquisitionB2bPercent"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('acquisitionB2bPercent', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>

							<Grid item xs={12} md={4}>
								<CerebroLabel id="acquisitionB2cPercent">B2C</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="acquisitionB2cPercent"
										as={CerebroMaskedInput}
										name="acquisitionB2cPercent"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('acquisitionB2cPercent', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>

							<Grid item xs={12} md={4}>
								<CerebroLabel id="acquisitionB2gPercent">B2G</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="acquisitionB2gPercent"
										as={CerebroMaskedInput}
										name="acquisitionB2gPercent"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('acquisitionB2gPercent', errors, touched)}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						<CerebroLabel
							htmlFor="acquisitionCustomerConcentrationsMoreThan10Percent"
							sx={{ marginTop: 3 }}
						>
							Does the company have any customer concentrations that account for more than 10% of revenue?
						</CerebroLabel>
						<Field
							id="acquisitionCustomerConcentrationsMoreThan10Percent"
							as={CerebroYesNo}
							name="acquisitionCustomerConcentrationsMoreThan10Percent"
							disabled={isSubmitting}
						/>

						{values.acquisitionCustomerConcentrationsMoreThan10Percent && (
							<>
								<CerebroLabel htmlFor="acquisitionCustomerConcentrationDetails" sx={{ marginTop: 3 }}>
									Please describe
								</CerebroLabel>
								<Field
									id="acquisitionCustomerConcentrationDetails"
									as={CerebroTextarea}
									name="acquisitionCustomerConcentrationDetails"
									disabled={isSubmitting}
								/>
							</>
						)}

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save &amp; Continue
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroPageContainer>
	);
};

function validateForm(values: CompanyBorrowerFormValues) {
	const errors: CompanyBorrowerFormErrors = {};

	if (!values.acquisitionIndustry) {
		errors.acquisitionIndustry = "Please indicate the company's industry";
	}

	if (!values.acquisitionYearsInOperation) {
		errors.acquisitionYearsInOperation = 'Please indicate years of operation';
	}

	if (!values.acquisitionUsBasedEmployees) {
		errors.acquisitionUsBasedEmployees = 'Please indicate the # of US employees';
	}

	return errors;
}

type CompanyBorrowerFormErrors = FormikErrors<CompanyBorrowerFormValues>;
type DealMutationVariables = FormikUtils.MutateOnSubmitVariables<CompanyBorrowerFormValues>;
interface CompanyBorrowerFormValues {
	acquisitionNaicsCode: NaicsCode | null;
	acquisitionCountryType: CountryTypes;
	acquisitionStateCode: string;
	acquisitionPostalCode: string;
	geographies: string[];
	acquisitionIndustry: Industry | null;
	acquisitionYearsInOperation: string;
	acquisitionUsBasedEmployees: string;
	acquisitionNonUsBasedEmployees: string;
	acquisitionInstitutionalInvestor: boolean;
	acquisitionB2bPercent: string;
	acquisitionB2cPercent: string;
	acquisitionB2gPercent: string;
	acquisitionCustomerConcentrationsMoreThan10Percent: boolean;
	acquisitionCustomerConcentrationDetails: string;
}
